import React, { useCallback, useState, useEffect } from "react";
import ReactDataGrid from "react-data-grid";

import { useField } from "react-final-form";
import { filter, groupBy, find, sortBy } from "lodash";
import { useNotify } from "react-admin";

interface IAreaDatagrid {
  edit?: boolean;
  name: string;
  areas: any[];
  frequency: number;
  isDisabled?: boolean;
}

enum EWayType {
  oneWay = "one_way",
  twoWay = "two_way",
}
interface IRow {
  areaId: number;
  area: string;
  oneWayId?: number | undefined;
  twoWayId?: number | undefined;
  areaData?: object | undefined;
  oneWay: number;
  twoWay: number;
}

interface IRateChartInsert {
  id?: number;
  areaId: number;
  wayType: EWayType;
  frequencyPerWeek: number;
  fullPrice: number;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

const columns = [
  { key: "area", name: "Area" },
  {
    key: "oneWay",
    name: "1 Way",
    editable: true,
    formatter: PriceFormatter,
  },
  {
    key: "twoWay",
    name: "2 Way",
    editable: true,
    formatter: PriceFormatter,
  },
];

const EditableAreaDatagrid: React.FC<IAreaDatagrid> = ({
  name,
  areas,
  frequency,
  edit,
  isDisabled,
}) => {
  const [rows, setRows] = useState<IRow[] | []>([]);
  const [isLoaded, setLoaded] = useState(false);
  const notify = useNotify();

  const {
    input: { onChange },
  } = useField(name);

  //only used for update case
  const rateChartItems = useField("rateChartItems");

  //populate rows data with updated areas data from parent component
  useEffect(() => {
    let rowData: IRow[] = [];

    if (!edit) {
      //prefill empty placeholder for create-case
      areas.forEach(({ name, id }: any) => {
        const data: IRow = { areaId: id, area: name, oneWay: 0, twoWay: 0 };
        rowData.push(data);
      });
    }

    setRows(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  // ratechart items values only for first time if it is not undefined
  useEffect(() => {
    if (areas.length !== 0) {
      if (!isLoaded && rateChartItems.input.value !== "") {
        let rowData: IRow[] = [];

        areas.forEach((area: any) => {
          const data: IRow = {
            oneWayId: null,
            twoWayId: null,
            areaId: area.id,
            area: area.name,
            areaData: area,
            oneWay: 0,
            twoWay: 0,
          };
          rowData.push(data);
        });

        const filteredItems = filter(
          rateChartItems.input.value,
          (item: IRateChartItem) => {
            return item.frequencyPerWeek === frequency;
          }
        );

        const groupedItems: any = groupBy(filteredItems, "areaId");

        Object.keys(groupedItems).forEach((key: any) => {
          const oneWayItem = find(groupedItems[key], {
            wayType: EWayType.oneWay,
          });
          const twoWayItem = find(groupedItems[key], {
            wayType: EWayType.twoWay,
          });
          const areaContext = find(areas, { id: oneWayItem.areaId });

          const data: IRow = {
            oneWayId: oneWayItem.id,
            twoWayId: twoWayItem.id,
            areaId: oneWayItem.areaId,
            area: areaContext?.name,
            areaData: areaContext,
            oneWay: oneWayItem.fullPrice.toFixed(2) / 100,
            twoWay: twoWayItem.fullPrice.toFixed(2) / 100,
          };

          rowData.forEach((row, index, rowDataArray) => {
            if (row.areaId === oneWayItem.areaId) {
              rowDataArray[index] = data;
            }
          });
        });

        rowData = sortBy(rowData, [
          (o: any) => {
            return o.areaData.maxDistanceInMeters;
          },
        ]);
        setRows(rowData);
        setLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateChartItems.input.value, areas]);

  //update react-form data provider if there is a change in rows
  useEffect(() => {
    const insertData: IRateChartInsert[] = [];

    rows.forEach((rowItem: IRow) => {
      let oneWay: IRateChartInsert;
      let twoWay: IRateChartInsert;

      oneWay = {
        id: rowItem.oneWayId ? rowItem.oneWayId : undefined,
        areaId: rowItem.areaId,
        frequencyPerWeek: frequency,
        wayType: EWayType.oneWay,
        fullPrice: Number(Math.round(rowItem.oneWay * 100).toFixed(2)),
      };

      twoWay = {
        id: rowItem.twoWayId ? rowItem.twoWayId : undefined,
        areaId: rowItem.areaId,
        frequencyPerWeek: frequency,
        wayType: EWayType.twoWay,
        fullPrice: Number(Math.round(rowItem.twoWay * 100).toFixed(2)),
      };

      insertData.push(oneWay);
      insertData.push(twoWay);
    });

    onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const onGridRowsUpdated = useCallback(
    ({ fromRow, toRow, updated }: any) => {
      const updatedRows = rows.slice();
      for (let i = fromRow; i <= toRow; i++) {
        if (updated.hasOwnProperty("twoWay")) {
          if (Number(updatedRows[i].oneWay) > Number(updated.twoWay)) {
            return notify(`1 Way Cannot more than 2 Way`, "warning");
          }
        }

        if (updated.hasOwnProperty("oneWay") && updatedRows[i].twoWay !== 0) {
          if (Number(updatedRows[i].twoWay) < Number(updated.oneWay)) {
            return notify(`1 Way Cannot more than 2 Way`, "warning");
          }
        }

        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setRows(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRows, rows]
  );

  if (areas.length === 0) {
    return <h4>Areas data is not set.</h4>;
  }

  if (rows.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => rows[i]}
        rowsCount={rows.length}
        minHeight={400}
        enableCellSelect={isDisabled ? false : true}
        onGridRowsUpdated={onGridRowsUpdated}
      />
    );
  }
};

export default EditableAreaDatagrid;
